import { useState, useRef } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Upload, X } from "lucide-react";
import { shotsApi } from "@/lib/api";
import { toast } from "sonner";

const FIELDS = [
  ["scene", "Scene"],
  ["shot_number", "Shot #"],
  ["shot_size", "Shot Size"],
  ["camera_angle", "Camera Angle"],
  ["lens", "Lens"],
  ["location", "Location"],
  ["talent", "Talent"],
  ["props", "Props"],
  ["duration", "Duration"],
  ["scheduled_date", "Scheduled Date", "date"],
];

export default function ShotEditDialog({ shot, onClose, onSaved }) {
  const [form, setForm] = useState({ ...shot });
  const [saving, setSaving] = useState(false);
  const fileRef = useRef();

  const handleChange = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 3 * 1024 * 1024) {
      toast.error("Image must be under 3MB");
      return;
    }
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        const updated = await shotsApi.uploadImage(shot.id, reader.result);
        setForm((f) => ({ ...f, image_data: updated.image_data }));
        toast.success("Storyboard attached");
      } catch (err) {
        toast.error("Upload failed");
      }
    };
    reader.readAsDataURL(file);
  };

  const handleRemoveImage = async () => {
    try {
      const updated = await shotsApi.update(shot.id, { image_data: "" });
      setForm((f) => ({ ...f, image_data: "" }));
      onSaved(updated);
    } catch {
      toast.error("Failed to remove image");
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {};
      [
        "scene",
        "shot_number",
        "description",
        "shot_size",
        "camera_angle",
        "lens",
        "location",
        "talent",
        "props",
        "duration",
        "scheduled_date",
        "notes",
        "status",
      ].forEach((k) => (payload[k] = form[k] ?? ""));
      const updated = await shotsApi.update(shot.id, payload);
      onSaved(updated);
      toast.success("Shot updated");
    } catch (e) {
      toast.error("Failed to save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-zinc-950 border-zinc-800 text-zinc-100 sm:max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl font-light">
            Edit Shot
          </DialogTitle>
          <DialogDescription className="text-zinc-500">
            Update shot details, schedule, notes, and storyboard.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label className="text-overline text-zinc-500">Description</Label>
            <Textarea
              data-testid="edit-description"
              value={form.description || ""}
              onChange={(e) => handleChange("description", e.target.value)}
              className="bg-zinc-950 border-zinc-800 focus-visible:ring-amber-500 mt-1.5 min-h-[80px]"
              placeholder="Action / framing description…"
            />
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {FIELDS.map(([k, label, type]) => (
              <div key={k}>
                <Label className="text-overline text-zinc-500">{label}</Label>
                <Input
                  data-testid={`edit-${k}`}
                  type={type || "text"}
                  value={form[k] || ""}
                  onChange={(e) => handleChange(k, e.target.value)}
                  className="bg-zinc-950 border-zinc-800 focus-visible:ring-amber-500 mt-1.5"
                />
              </div>
            ))}
          </div>

          <div>
            <Label className="text-overline text-zinc-500">Notes</Label>
            <Textarea
              data-testid="edit-notes"
              value={form.notes || ""}
              onChange={(e) => handleChange("notes", e.target.value)}
              className="bg-zinc-950 border-zinc-800 focus-visible:ring-amber-500 mt-1.5 min-h-[60px]"
            />
          </div>

          <div>
            <Label className="text-overline text-zinc-500">Storyboard</Label>
            <div className="mt-2 flex items-start gap-4">
              {form.image_data ? (
                <div className="relative">
                  <img
                    src={form.image_data}
                    alt="storyboard"
                    className="w-40 h-28 object-cover rounded-sm border border-zinc-800"
                  />
                  <button
                    data-testid="remove-image-btn"
                    onClick={handleRemoveImage}
                    className="absolute -top-2 -right-2 p-1 bg-zinc-950 border border-zinc-800 rounded-full text-zinc-400 hover:text-rose-400"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ) : (
                <div
                  onClick={() => fileRef.current?.click()}
                  data-testid="upload-image-dropzone"
                  className="w-40 h-28 border border-dashed border-zinc-800 rounded-sm flex flex-col items-center justify-center text-zinc-600 hover:text-amber-500 hover:border-amber-500/40 cursor-pointer transition-colors"
                >
                  <Upload className="w-5 h-5 mb-1" />
                  <span className="text-xs">Upload image</span>
                </div>
              )}
              <input
                ref={fileRef}
                type="file"
                accept="image/*"
                onChange={handleImage}
                className="hidden"
              />
              <div className="text-xs text-zinc-500 max-w-xs leading-relaxed">
                Attach a reference frame or storyboard sketch.
                <br />
                Max 3MB · PNG, JPG, WEBP.
              </div>
            </div>
          </div>
        </div>

        <DialogFooter className="pt-4">
          <Button
            variant="outline"
            onClick={onClose}
            className="bg-transparent border-zinc-800 text-zinc-300 hover:bg-zinc-900 hover:text-zinc-100"
          >
            Cancel
          </Button>
          <Button
            data-testid="save-shot-btn"
            onClick={handleSave}
            disabled={saving}
            className="bg-amber-500 text-black hover:bg-amber-400 rounded-sm"
          >
            {saving ? "Saving…" : "Save Changes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
